import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Trophy, Plus, Edit2, Archive, Check, Eye, Flag, Trash2, RotateCcw, ExternalLink, X, Save, Upload, Link2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const emptyChallenge = {
  title: '',
  description: '',
  instructions: '',
  demo_video_url: '',
  demo_thumbnail_url: '',
  scoring_type: 'count',
  starts_at: '',
  ends_at: '',
};

const PlatformAdminChallenges = () => {
  const { getAuthHeaders } = useAuth();
  const [tab, setTab] = useState('challenges'); // challenges | review
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyChallenge);
  const [attempts, setAttempts] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [challengeFilter, setChallengeFilter] = useState('');

  // Demo video upload
  const [demoSource, setDemoSource] = useState('link'); // 'link' | 'upload'
  const [demoUploading, setDemoUploading] = useState(false);
  const [demoUploadProgress, setDemoUploadProgress] = useState(0);

  const uploadDemoVideo = async (file) => {
    if (!file) return;
    // Soft client-side guard (server enforces 200 MB)
    if (file.size > 200 * 1024 * 1024) {
      toast.error('File too large (max 200 MB)');
      return;
    }
    if (!file.type.startsWith('video/')) {
      toast.error('Please select a video file');
      return;
    }

    setDemoUploading(true);
    setDemoUploadProgress(0);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/challenges/admin/upload-demo-video`, fd, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          if (e.total) setDemoUploadProgress(Math.round((e.loaded / e.total) * 100));
        },
      });
      // Save the public stream URL as the challenge's demo_video_url + thumbnail.
      const absoluteVideo = `${BACKEND}${res.data.video_url}`;
      const absoluteThumb = res.data.thumbnail_url ? `${BACKEND}${res.data.thumbnail_url}` : '';
      setForm((f) => ({ ...f, demo_video_url: absoluteVideo, demo_thumbnail_url: absoluteThumb }));
      toast.success(absoluteThumb ? 'Demo video + thumbnail uploaded' : 'Demo video uploaded (no thumb)');
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Upload failed';
      toast.error(msg);
    } finally {
      setDemoUploading(false);
      setDemoUploadProgress(0);
    }
  };

  const fetchChallenges = async () => {
    try {
      const res = await axios.get(`${API}/challenges/admin?include_archived=true`, { headers: getAuthHeaders() });
      setChallenges(res.data);
    } catch (e) { toast.error('Failed to load challenges'); }
    setLoading(false);
  };

  const fetchAttempts = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (challengeFilter) params.set('challenge_id', challengeFilter);
      const res = await axios.get(`${API}/challenges/admin/attempts?${params.toString()}`, { headers: getAuthHeaders() });
      setAttempts(res.data);
    } catch (e) { toast.error('Failed to load attempts'); }
  };

  useEffect(() => {
    if (tab === 'challenges') fetchChallenges();
    if (tab === 'review') fetchAttempts();
  }, [tab, statusFilter, challengeFilter]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyChallenge, starts_at: nowInputDate(), ends_at: nowInputDate(7) });
    setDemoSource('link');
    setShowForm(true);
  };

  const openEdit = (ch) => {
    setEditing(ch);
    setForm({
      title: ch.title || '',
      description: ch.description || '',
      instructions: ch.instructions || '',
      demo_video_url: ch.demo_video_url || '',
      demo_thumbnail_url: ch.demo_thumbnail_url || '',
      scoring_type: ch.scoring_type || 'count',
      starts_at: toInputDate(ch.starts_at),
      ends_at: toInputDate(ch.ends_at),
    });
    // If existing URL points at our own backend, default to Upload tab (read-only preview)
    const isOurs = (ch.demo_video_url || '').includes('/api/challenges/video/');
    setDemoSource(isOurs ? 'upload' : 'link');
    setShowForm(true);
  };

  const save = async () => {
    if (!form.title.trim() || !form.description.trim()) {
      toast.error('Title and description are required');
      return;
    }
    try {
      const payload = {
        ...form,
        starts_at: new Date(form.starts_at).toISOString(),
        ends_at: new Date(form.ends_at).toISOString(),
      };
      if (editing) {
        await axios.put(`${API}/challenges/admin/${editing.id}`, payload, { headers: getAuthHeaders() });
        toast.success('Challenge updated');
      } else {
        await axios.post(`${API}/challenges/admin`, payload, { headers: getAuthHeaders() });
        toast.success('Challenge created');
      }
      setShowForm(false);
      fetchChallenges();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const activate = async (id) => {
    await axios.post(`${API}/challenges/admin/${id}/activate`, {}, { headers: getAuthHeaders() });
    toast.success('Challenge activated');
    fetchChallenges();
  };

  const archive = async (id) => {
    if (!window.confirm('Archive this challenge?')) return;
    await axios.post(`${API}/challenges/admin/${id}/archive`, {}, { headers: getAuthHeaders() });
    toast.success('Challenge archived');
    fetchChallenges();
  };

  const review = async (attemptId, status) => {
    await axios.put(`${API}/challenges/admin/attempts/${attemptId}/review?status=${status}`, {}, { headers: getAuthHeaders() });
    toast.success(`Attempt ${status}`);
    fetchAttempts();
  };

  const editScore = async (attemptId, currentScore) => {
    const v = window.prompt('New score:', String(currentScore));
    if (v == null) return;
    const num = parseFloat(v);
    if (isNaN(num) || num < 0) return toast.error('Invalid score');
    await axios.put(`${API}/challenges/admin/attempts/${attemptId}/score?score=${num}`, {}, { headers: getAuthHeaders() });
    toast.success('Score updated');
    fetchAttempts();
  };

  return (
    <div className="space-y-6" data-testid="admin-challenges">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl uppercase tracking-tight flex items-center gap-3">
            <Trophy className="text-primary" /> Weekly Challenges
          </h1>
          <p className="text-muted-foreground text-sm">Curate the weekly competitive moment for Founding Beta users.</p>
        </div>
        {tab === 'challenges' && (
          <Button onClick={openCreate} className="bg-primary text-black hover:bg-primary/90 uppercase tracking-wider font-bold" data-testid="create-challenge-btn">
            <Plus size={16} className="mr-2" /> New Challenge
          </Button>
        )}
      </div>

      <div className="flex gap-2 border-b border-border">
        <button
          onClick={() => setTab('challenges')}
          className={`px-4 py-2 text-sm uppercase tracking-widest transition-colors ${tab === 'challenges' ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground'}`}
          data-testid="tab-challenges"
        >
          Challenges
        </button>
        <button
          onClick={() => setTab('review')}
          className={`px-4 py-2 text-sm uppercase tracking-widest transition-colors ${tab === 'review' ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground'}`}
          data-testid="tab-review"
        >
          Attempts Review
        </button>
      </div>

      {tab === 'challenges' && (
        <div className="space-y-3">
          {loading && <p className="text-muted-foreground text-sm">Loading...</p>}
          {!loading && challenges.length === 0 && (
            <div className="bg-card border border-border rounded-sm p-12 text-center">
              <Trophy className="mx-auto text-muted-foreground mb-3" size={32} />
              <p className="text-muted-foreground text-sm">No challenges yet. Create your first one to launch the weekly competition.</p>
            </div>
          )}
          {challenges.map((ch) => (
            <div key={ch.id} className={`bg-card border rounded-sm p-4 ${ch.is_active ? 'border-primary/50' : 'border-border/40'}`} data-testid={`challenge-row-${ch.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    {ch.is_active && <span className="text-[10px] bg-primary/20 text-primary px-2 py-0.5 rounded uppercase tracking-widest">Live</span>}
                    {ch.is_archived && <span className="text-[10px] bg-muted text-muted-foreground px-2 py-0.5 rounded uppercase tracking-widest">Archived</span>}
                    <span className="text-[10px] text-muted-foreground uppercase tracking-widest">{ch.scoring_type}</span>
                  </div>
                  <h3 className="font-heading text-lg">{ch.title}</h3>
                  <p className="text-sm text-muted-foreground line-clamp-2">{ch.description}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {new Date(ch.starts_at).toLocaleDateString()} → {new Date(ch.ends_at).toLocaleDateString()} · {ch.attempts_count || 0} attempts
                  </p>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {!ch.is_active && !ch.is_archived && (
                    <Button size="sm" onClick={() => activate(ch.id)} className="bg-primary text-black hover:bg-primary/90" data-testid={`activate-${ch.id}`}>
                      <Check size={14} className="mr-1" /> Activate
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => openEdit(ch)}><Edit2 size={14} /></Button>
                  {!ch.is_archived && (
                    <Button size="sm" variant="outline" onClick={() => archive(ch.id)} className="text-red-400 border-red-400/30 hover:bg-red-500/10"><Archive size={14} /></Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'review' && (
        <div className="space-y-3">
          <div className="flex gap-3 flex-wrap">
            <select value={challengeFilter} onChange={(e) => setChallengeFilter(e.target.value)} className="bg-card border border-border rounded-sm px-3 py-2 text-sm" data-testid="filter-challenge">
              <option value="">All challenges</option>
              {challenges.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
            </select>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="bg-card border border-border rounded-sm px-3 py-2 text-sm" data-testid="filter-status">
              <option value="">All statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="flagged">Flagged</option>
              <option value="removed">Removed</option>
            </select>
          </div>

          {attempts.length === 0 && (
            <div className="bg-card border border-border rounded-sm p-12 text-center">
              <p className="text-muted-foreground text-sm">No attempts match the filter.</p>
            </div>
          )}

          {attempts.map((a) => (
            <div key={a.id} className="bg-card border border-border rounded-sm p-4 flex items-center gap-4 flex-wrap" data-testid={`attempt-row-${a.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className={`text-[10px] px-2 py-0.5 rounded uppercase tracking-widest ${
                    a.admin_review_status === 'approved' ? 'bg-emerald-500/20 text-emerald-300' :
                    a.admin_review_status === 'removed' ? 'bg-red-500/20 text-red-300' :
                    a.admin_review_status === 'flagged' ? 'bg-amber-500/20 text-amber-300' :
                    'bg-muted text-muted-foreground'
                  }`}>{a.admin_review_status}</span>
                  {a.rank && <span className="text-[10px] bg-primary/10 text-primary px-2 py-0.5 rounded">#{a.rank}</span>}
                </div>
                <p className="font-medium">{a.athlete_name} <span className="text-xs text-muted-foreground">· {a.club_name}</span></p>
                <p className="text-xs text-muted-foreground">{new Date(a.submitted_at).toLocaleString()}</p>
              </div>
              <div className="font-heading text-2xl text-primary">{a.score}</div>
              <div className="flex gap-2 flex-wrap">
                <Button size="sm" variant="outline" onClick={() => window.open(a.video_url, '_blank')} data-testid={`view-video-${a.id}`}><ExternalLink size={14} /></Button>
                <Button size="sm" variant="outline" onClick={() => editScore(a.id, a.score)}><Edit2 size={14} /></Button>
                {a.admin_review_status !== 'approved' && (
                  <Button size="sm" variant="outline" className="text-emerald-300 border-emerald-500/30" onClick={() => review(a.id, 'approved')} data-testid={`approve-${a.id}`}><Check size={14} /></Button>
                )}
                {a.admin_review_status !== 'flagged' && (
                  <Button size="sm" variant="outline" className="text-amber-300 border-amber-500/30" onClick={() => review(a.id, 'flagged')}><Flag size={14} /></Button>
                )}
                {a.admin_review_status !== 'removed' && (
                  <Button size="sm" variant="outline" className="text-red-400 border-red-500/30" onClick={() => review(a.id, 'removed')} data-testid={`remove-${a.id}`}><Trash2 size={14} /></Button>
                )}
                {a.admin_review_status === 'removed' && (
                  <Button size="sm" variant="outline" className="text-primary border-primary/30" onClick={() => review(a.id, 'pending')}><RotateCcw size={14} /></Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="challenge-form">
          <div className="bg-card border border-primary/30 rounded-sm max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-xl uppercase">{editing ? 'Edit' : 'Create'} Challenge</h2>
              <button onClick={() => setShowForm(false)} className="text-muted-foreground hover:text-foreground"><X size={20} /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-widest text-muted-foreground mb-1 block">Title *</label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Juggling Master" data-testid="form-title" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-muted-foreground mb-1 block">Description *</label>
                <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="w-full bg-card border border-border rounded-sm px-3 py-2 text-sm" placeholder="Quick pitch of the challenge" data-testid="form-description" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-muted-foreground mb-1 block">Instructions</label>
                <textarea value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })} rows={4} className="w-full bg-card border border-border rounded-sm px-3 py-2 text-sm" placeholder="What the athlete should do, step by step" data-testid="form-instructions" />
              </div>
              <div data-testid="form-demo-video-section">
                <label className="text-xs uppercase tracking-widest text-muted-foreground mb-1 block">Demo Video</label>

                {/* Source toggle */}
                <div className="flex gap-1 mb-2 bg-muted/40 p-1 rounded-sm w-fit">
                  <button
                    type="button"
                    onClick={() => setDemoSource('link')}
                    className={`px-3 py-1 text-xs uppercase tracking-wider rounded-sm flex items-center gap-1.5 transition ${
                      demoSource === 'link' ? 'bg-primary text-black font-bold' : 'text-muted-foreground hover:text-foreground'
                    }`}
                    data-testid="demo-source-link"
                  >
                    <Link2 size={12} /> Link
                  </button>
                  <button
                    type="button"
                    onClick={() => setDemoSource('upload')}
                    className={`px-3 py-1 text-xs uppercase tracking-wider rounded-sm flex items-center gap-1.5 transition ${
                      demoSource === 'upload' ? 'bg-primary text-black font-bold' : 'text-muted-foreground hover:text-foreground'
                    }`}
                    data-testid="demo-source-upload"
                  >
                    <Upload size={12} /> Upload
                  </button>
                </div>

                {demoSource === 'link' && (
                  <Input
                    value={form.demo_video_url}
                    onChange={(e) => setForm({ ...form, demo_video_url: e.target.value, demo_thumbnail_url: '' })}
                    placeholder="YouTube or direct video link"
                    data-testid="form-demo"
                  />
                )}

                {demoSource === 'upload' && (
                  <div className="space-y-2">
                    <label
                      className={`flex items-center justify-center gap-2 w-full border-2 border-dashed border-border rounded-sm p-4 text-sm cursor-pointer transition ${
                        demoUploading ? 'opacity-60 pointer-events-none' : 'hover:border-primary hover:bg-primary/5'
                      }`}
                      data-testid="demo-upload-dropzone"
                    >
                      <input
                        type="file"
                        accept="video/*"
                        className="hidden"
                        disabled={demoUploading}
                        onChange={(e) => {
                          const f = e.target.files && e.target.files[0];
                          if (f) uploadDemoVideo(f);
                          // allow re-uploading the same file
                          e.target.value = '';
                        }}
                        data-testid="demo-upload-input"
                      />
                      {demoUploading ? (
                        <>
                          <Loader2 size={16} className="animate-spin text-primary" />
                          <span className="text-primary">Uploading… {demoUploadProgress}%</span>
                        </>
                      ) : form.demo_video_url ? (
                        <>
                          <Check size={16} className="text-emerald-400" />
                          <span className="text-emerald-400">Demo uploaded — click to replace</span>
                        </>
                      ) : (
                        <>
                          <Upload size={16} className="text-muted-foreground" />
                          <span className="text-muted-foreground">Click to upload (max 200 MB, mp4/webm/mov)</span>
                        </>
                      )}
                    </label>

                    {form.demo_video_url && !demoUploading && (
                      <div className="space-y-2">
                        {form.demo_thumbnail_url && (
                          <div className="relative rounded-sm overflow-hidden border border-border bg-black aspect-video max-w-xs" data-testid="demo-thumbnail-preview">
                            <img
                              src={form.demo_thumbnail_url}
                              alt="Demo thumbnail"
                              className="w-full h-full object-cover"
                            />
                            <span className="absolute bottom-1 right-1 text-[10px] uppercase tracking-widest bg-black/70 text-primary px-1.5 py-0.5 rounded">
                              auto thumb
                            </span>
                          </div>
                        )}
                        <div className="flex items-center justify-between text-xs">
                          <a
                            href={form.demo_video_url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-primary hover:underline flex items-center gap-1"
                            data-testid="demo-preview-link"
                          >
                            <ExternalLink size={12} /> Preview video
                          </a>
                          <button
                            type="button"
                            onClick={() => setForm({ ...form, demo_video_url: '', demo_thumbnail_url: '' })}
                            className="text-muted-foreground hover:text-red-400 flex items-center gap-1"
                            data-testid="demo-clear-btn"
                          >
                            <Trash2 size={12} /> Remove
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-widest text-muted-foreground mb-1 block">Scoring Type</label>
                  <select value={form.scoring_type} onChange={(e) => setForm({ ...form, scoring_type: e.target.value })} className="w-full bg-card border border-border rounded-sm px-3 py-2 text-sm" data-testid="form-scoring">
                    <option value="count">Count (higher wins)</option>
                    <option value="time">Time (lower wins)</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-widest text-muted-foreground mb-1 block">Starts At</label>
                  <Input type="datetime-local" value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} data-testid="form-starts" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-muted-foreground mb-1 block">Ends At</label>
                  <Input type="datetime-local" value={form.ends_at} onChange={(e) => setForm({ ...form, ends_at: e.target.value })} data-testid="form-ends" />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
                <Button onClick={save} className="bg-primary text-black hover:bg-primary/90 uppercase tracking-wider font-bold" data-testid="form-save">
                  <Save size={14} className="mr-2" /> {editing ? 'Update' : 'Create'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// helpers
const toInputDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const nowInputDate = (addDays = 0) => {
  const d = new Date();
  d.setDate(d.getDate() + addDays);
  return toInputDate(d.toISOString());
};

export default PlatformAdminChallenges;
